import { useRef, useState, useEffect, useCallback } from 'react';
import classes from './useScreenReaderAnnouncement2.js';

/**
 * Sets up a live region in order to make screen reader announcements.
 * @param politeness - Sets the politeness level for `aria-live`.
 * @returns - A function used to send an announcement to the screen reader.
 */
function useScreenReaderAnnouncement(politeness) {
  const ariaLiveElementRef = useRef();
  const [messageQueue, setMessageQueue] = useState([]); // An array of messages where the first item in the array is the oldest message
  const [isAnnouncingMessage, setIsAnnouncingMessage] = useState(false);
  const delayTimer = useRef();

  // Creates the live region element
  useEffect(() => {
    ariaLiveElementRef.current = document.createElement('span');
    ariaLiveElementRef.current.classList.add(classes.ariaLiveElement);
    document.body.appendChild(ariaLiveElementRef.current);
    return () => {
      if (ariaLiveElementRef.current !== undefined) document.body.removeChild(ariaLiveElementRef.current);
    };
  }, []);

  // Sets the politeness of `aria-live`
  useEffect(() => {
    var _ariaLiveElementRef$c;
    if (politeness !== undefined) (_ariaLiveElementRef$c = ariaLiveElementRef.current) === null || _ariaLiveElementRef$c === void 0 || _ariaLiveElementRef$c.setAttribute('aria-live', politeness);
  }, [politeness]);

  // Announces oldest message in the queue
  useEffect(() => {
    if (!isAnnouncingMessage && messageQueue.length > 0) {
      var _ariaLiveElementRef$c2;
      setIsAnnouncingMessage(true);
      const oldestMessage = messageQueue[0];
      let delay;
      if (((_ariaLiveElementRef$c2 = ariaLiveElementRef.current) === null || _ariaLiveElementRef$c2 === void 0 ? void 0 : _ariaLiveElementRef$c2.textContent) === oldestMessage) {
        /*
         * If the message to be announced is the same as the previous message, the message needs to be reset.
         * The screen reader won't read the change unless the text has changed.
         */
        ariaLiveElementRef.current.textContent = '';
        delay = 200; // Gives the screen reader time to detect the new message (this number was chosen arbitrarily)
      } else {
        // If the message to be announced is different from the previous message, there is no need for a delay
        delay = 0;
      }
      delayTimer.current = setTimeout(() => {
        if (ariaLiveElementRef.current !== undefined) ariaLiveElementRef.current.textContent = oldestMessage;
        setMessageQueue(currentMessageQueue => currentMessageQueue.slice(1)); // Removes the oldest message (i.e. first item) from the message queue since it was just announced
        setIsAnnouncingMessage(false);
      }, delay);
    }
  }, [isAnnouncingMessage, messageQueue]);

  // Cleanup timeout to avoid memory leaks
  useEffect(() => () => {
    if (delayTimer.current) clearTimeout(delayTimer.current);
  }, []);

  /**
   * Sends a message for the screen reader to read.
   * @param message - The message that the screen reader will read.
   */
  const announceToScreenReader = useCallback(message => {
    setMessageQueue(currentMessageQueue => [...currentMessageQueue, message]);
  }, []);
  return announceToScreenReader;
}

export { useScreenReaderAnnouncement as default };
